import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Delete, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtEUR, INCOME, EXPENSE } from "@/lib/format";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function QuickAdd() {
  const qc = useQueryClient();
  const [type, setType] = useState("income"); // income | expense
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.get("/accounts").then((r) => r.data),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["categories", type],
    queryFn: () => api.get(`/categories?type=${type}`).then((r) => r.data),
  });

  useEffect(() => {
    if (!accountId && accounts.length) {
      const cash = accounts.find((a) => a.type === "cash");
      setAccountId((cash || accounts[0]).id);
    }
  }, [accounts, accountId]);

  useEffect(() => setCategory(null), [type]);

  const press = (k) => {
    setAmount((prev) => {
      if (k === "del") return prev.slice(0, -1);
      if (k === ",") return prev.includes(",") ? prev : (prev || "0") + ",";
      if (prev.includes(",") && prev.split(",")[1]?.length >= 2) return prev;
      return prev + k;
    });
  };

  const numeric = parseFloat((amount || "0").replace(",", "."));

  const submit = async () => {
    if (!numeric || numeric <= 0) return toast.error("Introduza um valor");
    if (!category) return toast.error("Escolha uma categoria");
    setSaving(true);
    try {
      await api.post("/transactions/quick", {
        type,
        amount: numeric,
        category,
        account_id: accountId,
      });
      toast.success(type === "income" ? "Honorário registado" : "Despesa registada");
      setAmount("");
      setCategory(null);
      qc.invalidateQueries();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const activeColor = type === "income" ? INCOME : EXPENSE;

  return (
    <div className="max-w-md mx-auto">
      <h1 className="font-heading text-2xl font-bold mb-4">Registo rápido</h1>

      {/* Toggle tipo */}
      <div className="grid grid-cols-2 gap-2 p-1 bg-secondary rounded-full mb-5" data-testid="type-toggle">
        <button
          onClick={() => setType("income")}
          data-testid="toggle-income"
          className={`h-11 rounded-full text-sm font-semibold transition-colors ${
            type === "income" ? "text-white" : "text-muted-foreground"
          }`}
          style={type === "income" ? { backgroundColor: INCOME } : {}}
        >
          Honorário recebido
        </button>
        <button
          onClick={() => setType("expense")}
          data-testid="toggle-expense"
          className={`h-11 rounded-full text-sm font-semibold transition-colors ${
            type === "expense" ? "text-white" : "text-muted-foreground"
          }`}
          style={type === "expense" ? { backgroundColor: EXPENSE } : {}}
        >
          Despesa a dinheiro
        </button>
      </div>

      {/* Montante */}
      <div className="text-center py-6 bg-card border border-border rounded-lg mb-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Montante</div>
        <div
          className="font-heading text-5xl font-extrabold"
          style={{ color: numeric ? activeColor : "#B5B2A8" }}
          data-testid="amount-display"
        >
          {amount ? fmtEUR(numeric) : "0,00 €"}
        </div>
      </div>

      {/* Categorias */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Categoria</div>
        <div className="flex flex-wrap gap-2" data-testid="category-chips">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.name)}
              data-testid={`cat-${c.name}`}
              className={`px-3 py-2 rounded-full text-sm border transition-colors ${
                category === c.name
                  ? "text-white border-transparent"
                  : "bg-card border-border text-foreground hover:bg-secondary"
              }`}
              style={category === c.name ? { backgroundColor: activeColor } : {}}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Conta */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Conta</div>
        <Select value={accountId || ""} onValueChange={setAccountId}>
          <SelectTrigger data-testid="account-select">
            <SelectValue placeholder="Conta" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name} ({fmtEUR(a.balance)})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Teclado numérico */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="numpad">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "0", "del"].map((k) => (
          <button
            key={k}
            onClick={() => press(k)}
            data-testid={`key-${k}`}
            className="h-14 rounded-lg bg-card border border-border text-xl font-semibold text-foreground hover:bg-secondary active:scale-[0.98] transition-transform flex items-center justify-center"
          >
            {k === "del" ? <Delete size={20} /> : k}
          </button>
        ))}
      </div>

      <button
        onClick={submit}
        disabled={saving}
        data-testid="quick-submit"
        className="w-full h-14 rounded-full text-white font-semibold text-lg flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-60"
        style={{ backgroundColor: activeColor }}
      >
        <Check size={22} /> {saving ? "A guardar…" : "Registar"}
      </button>
    </div>
  );
}
